// Developed by NoMilk. Uses the NM namespace (see snippets/js-variables.liquid)
// for its own data, and the theme's FoxTheme namespace for its shared utilities.
class FavoritesUtils {
  static cookieNamePrefix = 'hypertheme:favorites';
  static expiryInDays = 365;

  /**
   * Storage key is scoped per logged in customer so that two accounts
   * on the same browser/device never share a favorites list. Signed-out
   * visitors share a single "guest" list on that browser.
   * @returns {string}
   */
  static getStorageKey() {
    if (NM.customer && NM.customer.loggedIn) {
      return `${this.cookieNamePrefix}:${NM.customer.id}`;
    }
    return `${this.cookieNamePrefix}:guest`;
  }

  /**
   * @returns {array}
   */
  static getFavorites() {
    const key = this.getStorageKey();
    if (!key) return [];

    return FoxTheme.utils.getStorage(key) || [];
  }

  static setFavorites(products) {
    const key = this.getStorageKey();
    if (!key) return;

    if (products.length > 0) {
      FoxTheme.utils.setStorage(key, products, this.expiryInDays);
    } else {
      window.localStorage.removeItem(key);
    }
  }

  static getFavoritesCount() {
    return this.getFavorites().length;
  }

  /**
   * @param {*} productId
   * @returns {boolean}
   */
  static isProductInList(productId) {
    const products = this.getFavorites();
    return !!products.find((product) => product.id === productId);
  }

  /**
   * @param {*} productId
   * @param {*} productUrl
   * @returns {boolean} True if the product was added to the list.
   */
  static addToList(productId, productUrl) {
    if (!this.getStorageKey()) return false;

    const products = this.getFavorites();

    if (!products.some((product) => product.id === productId)) {
      products.push({ id: productId, url: productUrl });
    }

    this.setFavorites(products);
    this.updateFavoriteButtons(products);

    FoxTheme.pubsub.publish('favorites:add', { products, productId, productUrl });

    return true;
  }

  static removeFromList(productId) {
    let products = this.getFavorites();
    products = products.filter((product) => product.id !== productId);

    this.setFavorites(products);
    this.updateFavoriteButtons(products);

    FoxTheme.pubsub.publish('favorites:remove', { products, productId });
  }

  static toggle(productId, productUrl) {
    if (this.isProductInList(productId)) {
      this.removeFromList(productId);
      return false;
    }

    this.addToList(productId, productUrl);
    return true;
  }

  static clearList() {
    this.getFavorites().forEach((product) => this.removeFromList(product.id));
  }

  static updateFavoriteButtons(products = null) {
    products = products || this.getFavorites();
    document.querySelectorAll('favorite-button').forEach((button) => {
      const isActive = products.some((product) => product.id === button.productId);
      button.setActive(isActive);
    });
  }

  static getTemplateUrl(productUrl, queryObj = {}) {
    let url = productUrl;
    const separator = productUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams();

    for (const key in queryObj) {
      if (Object.prototype.hasOwnProperty.call(queryObj, key)) {
        params.append(key, queryObj[key]);
      }
    }

    if (Array.from(params).length > 0) {
      url += `${separator}${params.toString()}`;
    }

    return url;
  }

  static getFavoriteCardTemplateUrl(productUrl) {
    return this.getTemplateUrl(productUrl, {
      sections: 'product-favorite-card',
    });
  }
}

if (!customElements.get('favorite-button')) {
  customElements.define(
    'favorite-button',
    class FavoriteButton extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isActive: 'is-active',
        };

        this.buttonEl = this.querySelector('button');
        this.onClickHandler = this.onClick.bind(this);
      }

      connectedCallback() {
        this.buttonEl.addEventListener('click', this.onClickHandler);
        this.setActive(FavoritesUtils.isProductInList(this.productId));
      }

      disconnectedCallback() {
        this.buttonEl.removeEventListener('click', this.onClickHandler);
      }

      get productId() {
        return this.dataset.productId;
      }

      get productUrl() {
        return this.dataset.productUrl;
      }

      onClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const isActive = FavoritesUtils.toggle(this.productId, this.productUrl);
        this.setActive(isActive);
      }

      setActive(isActive) {
        this.classList.toggle(this.classes.isActive, isActive);
        this.buttonEl.setAttribute('aria-pressed', isActive);

        const label = isActive
          ? NM.favoritesStrings.removeFromFavorites
          : NM.favoritesStrings.addToFavorites;
        this.buttonEl.setAttribute('aria-label', label);
      }
    }
  );
}

if (!customElements.get('favorite-list')) {
  customElements.define(
    'favorite-list',
    class FavoriteList extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          grid: '.favorite-list__grid',
          empty: '.favorite-list__empty',
        };

        this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
      }

      connectedCallback() {
        this.removeUnsubscriber = FoxTheme.pubsub.subscribe('favorites:remove', this.onItemRemoved.bind(this));
        this.render();
      }

      disconnectedCallback() {
        if (this.removeUnsubscriber) this.removeUnsubscriber();
      }

      async render() {
        const products = FavoritesUtils.getFavorites();

        if (products.length === 0) {
          this.toggleEmptyState(true);
          return;
        }

        this.toggleEmptyState(false);

        const cardHtmls = await this.getCards(products);
        this.elements.grid.innerHTML = cardHtmls.join('');
      }

      async getCards(products) {
        const tmps = [];

        await Promise.all(
          products.map(async (product) => {
            try {
              const response = await fetch(FavoritesUtils.getFavoriteCardTemplateUrl(product.url));

              if (response.status === 404) {
                // Product was actually deleted/unpublished since it was favorited.
                FavoritesUtils.removeFromList(product.id);
                return;
              }

              if (!response.ok) return;

              const data = await response.json();
              const parser = new DOMParser();
              const doc = parser.parseFromString(data['product-favorite-card'] || '', 'text/html');
              const itemHtml = doc.querySelector('.shopify-section');

              if (itemHtml) {
                tmps[product.id] = itemHtml.innerHTML;
              }
            } catch (error) {
              // Transient network/parsing error - keep the item in the list and just skip rendering it this time.
              console.error(error);
            }
          })
        );

        const returns = [];
        products.forEach((product) => {
          if (tmps[product.id]) returns.push(tmps[product.id]);
        });

        return returns;
      }

      onItemRemoved() {
        this.render();
      }

      toggleEmptyState(isEmpty) {
        this.elements.empty.classList.toggle('hidden', !isEmpty);
        this.elements.grid.classList.toggle('hidden', isEmpty);
      }
    }
  );
}
